import { Autocomplete, Box, Button, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { Styles } from 'common/types';
import { Modal } from 'common/ui/modal';
import { ChangeEvent, ReactElement, SyntheticEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { asinRecommendationActions } from 'modules/asin-recommendation/store/actions/actions';
import { asinRecommendationSelectors } from 'modules/asin-recommendation/store/selectors';
import { usePermission } from 'common/hooks';
import { PERMISSIONS } from 'common/constants/permissions';

const styles: Styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 1.5 },
  fieldContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  minMaxContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '70%',
    gap: 1.5,
  },
  field: { width: '70%' },
  btnContainer: { display: 'flex', justifyContent: 'flex-end', gap: 1.5 },
};

interface Props {
  isOpen: boolean;
  type?: 'grid' | 'list';
  handleApply: () => void;
  handleReset: () => void;
  onClose: () => void;
}

export function FiltersModal({ isOpen, type, onClose, handleApply, handleReset }: Props): ReactElement {
  const dispatch = useDispatch();

  const admin = usePermission(PERMISSIONS.GET_PUBLISHERS);

  const initialFilters = useSelector(asinRecommendationSelectors.selectInitialFilters);
  const filters = useSelector(asinRecommendationSelectors.selectFilters);

  const handleParentCategoryChange = (vent: SyntheticEvent<Element, Event>, value: string[]) => {
    dispatch(asinRecommendationActions.filters.setParentCategory(value));
  };

  const handleCategoryChange = (event: SyntheticEvent<Element, Event>, value: string[]) => {
    dispatch(asinRecommendationActions.filters.setCategory(value));
  };

  const handleBrandChange = (event: SyntheticEvent<Element, Event>, value: string[]) => {
    dispatch(asinRecommendationActions.filters.setBrand(value));
  };

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(asinRecommendationActions.filters.setType(e.target.value));
  };

  const handleStatusChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(asinRecommendationActions.filters.setStatus(e.target.value));
  };

  const handleSeasonalityChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(asinRecommendationActions.filters.setSeasonality(e.target.value));
  };

  const handleInStockChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(asinRecommendationActions.filters.setInStock(e.target.value));
  };

  const handlePriceMinChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setPriceMin(e.target.value));
  };

  const handlePriceMaxChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setPriceMax(e.target.value));
  };

  const handleEarnMinChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setEarnMin(e.target.value));
  };

  const handleEarnMaxChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setEarnMax(e.target.value));
  };

  const handleDiscountMinChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setDiscountMin(Number(e.target.value)));
  };

  const handleDiscountMaxChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setDiscountMax(Number(e.target.value)));
  };

  const handleRatingMinChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setRatingMin(Number(e.target.value)));
  };

  const handleRatingMaxChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(asinRecommendationActions.filters.setRatingMax(Number(e.target.value)));
  };

  useEffect(() => {
    dispatch(asinRecommendationActions.filters.resetFilters());
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <Modal open={isOpen} onClose={onClose} title="Filters" maxWidth="sm">
      <Box sx={styles.container}>
        <Box sx={styles.fieldContainer}>
          <Typography>Parent Categories</Typography>
          <Autocomplete
            sx={styles.field}
            multiple
            options={initialFilters.parent_categories.map(category => category.name)}
            size="small"
            getOptionLabel={option => (option === null ? 'Other' : option)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option === null ? 'Other' : option}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={params => (
              <TextField {...params} variant="outlined" label="Parent Categories" placeholder="Parent Categories" />
            )}
            onChange={handleParentCategoryChange}
            value={filters.parent_categories}
          />
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Categories</Typography>
          <Autocomplete
            sx={styles.field}
            multiple
            options={
              filters.parent_categories.length > 0
                ? initialFilters.categories
                    .filter(cd => filters.parent_categories.includes(cd.parent_category.name))
                    .flatMap(cd => cd.subcategories)
                : initialFilters.categories.flatMap(cd => cd.subcategories)
            }
            size="small"
            getOptionLabel={option => (option === null ? 'Other' : option)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option === null ? 'Other' : option}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={params => (
              <TextField {...params} variant="outlined" label="Categories" placeholder="Categories" />
            )}
            onChange={handleCategoryChange}
            value={filters.categories}
          />
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Product Discount</Typography>
          <Box sx={styles.minMaxContainer}>
            <TextField
              label="Product discount min %"
              type="number"
              size="small"
              value={filters.discount_min || 0}
              InputLabelProps={{ shrink: true }}
              defaultValue={initialFilters.discount_filters.min}
              onChange={handleDiscountMinChange}
            />
            <TextField
              label="Product price max %"
              type="number"
              size="small"
              value={filters.discount_max || 0}
              InputLabelProps={{ shrink: true }}
              defaultValue={initialFilters.discount_filters.max}
              onChange={handleDiscountMaxChange}
            />
          </Box>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Product Rating</Typography>
          <Box sx={styles.minMaxContainer}>
            <TextField
              label="Product min rating"
              type="number"
              size="small"
              value={filters.rating_min || 0}
              InputLabelProps={{ shrink: true }}
              onChange={handleRatingMinChange}
              defaultValue={initialFilters.rating_filters.min}
            />
            <TextField
              label="Product max rating"
              type="number"
              size="small"
              value={filters.rating_max || 5}
              InputLabelProps={{ shrink: true }}
              onChange={handleRatingMaxChange}
              defaultValue={initialFilters.rating_filters.max}
            />
          </Box>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Brand</Typography>
          <Autocomplete
            sx={styles.field}
            multiple
            options={initialFilters.brands}
            size="small"
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
              ))
            }
            renderInput={params => <TextField {...params} variant="outlined" label="Brands" placeholder="Brands" />}
            onChange={handleBrandChange}
            value={filters.brand}
          />
        </Box>
        {admin && (
          <Box sx={styles.fieldContainer}>
            <Typography>Product Type</Typography>
            <TextField
              select
              color="primary"
              sx={styles.field}
              value={filters.type}
              size="small"
              label="Types"
              onChange={handleTypeChange}
            >
              <MenuItem value="all">All</MenuItem>
              {initialFilters.types?.map(type => (
                <MenuItem key={type} value={type}>
                  {type === 'sr_products' ? 'SR' : 'Attribution'}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <Box sx={styles.fieldContainer}>
          <Typography>Status</Typography>
          <TextField
            select
            color="primary"
            sx={styles.field}
            value={filters.status}
            size="small"
            label="Status"
            onChange={handleStatusChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">In article</MenuItem>
            <MenuItem value="inactive">Not in article</MenuItem>
          </TextField>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Seasonality</Typography>
          <TextField
            select
            color="primary"
            sx={styles.field}
            value={filters.seasons}
            size="small"
            label="Seasonality"
            onChange={handleSeasonalityChange}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="current_month">Current Month</MenuItem>
            <MenuItem value="next_month">Next Month</MenuItem>
            <MenuItem value="next_three_months">Next Three Months</MenuItem>
            <MenuItem value="next_six_months">Next Six Months</MenuItem>
          </TextField>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>In Stock</Typography>
          <TextField
            select
            color="primary"
            sx={styles.field}
            value={filters.in_stock}
            size="small"
            label="In Stock"
            onChange={handleInStockChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">In Stock</MenuItem>
            <MenuItem value="false">Out of Stock</MenuItem>
          </TextField>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Product Price</Typography>
          <Box sx={styles.minMaxContainer}>
            <TextField
              label="Product price min %"
              type="number"
              size="small"
              value={filters.price_min || 0}
              InputLabelProps={{ shrink: true }}
              onChange={handlePriceMinChange}
            />
            <TextField
              label="Product price max %"
              type="number"
              size="small"
              value={filters.price_max || 0}
              InputLabelProps={{ shrink: true }}
              onChange={handlePriceMaxChange}
            />
          </Box>
        </Box>
        <Box sx={styles.fieldContainer}>
          <Typography>Opportunity Earn %</Typography>
          <Box sx={styles.minMaxContainer}>
            <TextField
              label="Opportunity earn min %"
              type="number"
              size="small"
              value={filters.earn_min || 0}
              InputLabelProps={{ shrink: true }}
              onChange={handleEarnMinChange}
            />
            <TextField
              label="Opportunity earn max %"
              type="number"
              size="small"
              value={filters.earn_max || 0}
              InputLabelProps={{ shrink: true }}
              onChange={handleEarnMaxChange}
            />
          </Box>
        </Box>
        <Box sx={styles.btnContainer}>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
